import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { ActionsDropdown } from "@/components/ui/actions-dropdown";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useSoftwareUpdatePolicies } from "@/hooks/useSoftwareUpdates";
import { Plus, Eye, Edit, Copy, Trash2, Loader2 } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as policiesApi from "@/services/policiesApi";
import { toast } from "sonner";

function formatDate(iso?: string) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}

export function PoliciesTab() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { policies, isLoading, error } = useSoftwareUpdatePolicies();
  const [toDelete, setToDelete] = useState<{ id: string; name: string } | null>(null);

  const del = useMutation({
    mutationFn: (id: string) => policiesApi.deletePolicy(id),
    onSuccess: () => {
      toast.success("Software update policy deleted");
      qc.invalidateQueries({ queryKey: ["policies"] });
      setToDelete(null);
    },
    onError: () => toast.error("Failed to delete software update policy"),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Policies that target Apple software updates across your fleet.
        </p>
        <Button size="sm" onClick={() => navigate("/software-updates/policies/new")}>
          <Plus className="mr-1 h-4 w-4" aria-hidden="true" /> New policy
        </Button>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Mode</TableHead>
              <TableHead>Updated</TableHead>
              <TableHead className="w-12 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Skeleton className="h-6 w-full" />
                </TableCell>
              </TableRow>
            )}
            {!isLoading && error && (
              <TableRow>
                <TableCell colSpan={4} className="text-sm text-destructive">
                  Failed to load policies.
                </TableCell>
              </TableRow>
            )}
            {!isLoading && !error && policies.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="text-sm text-muted-foreground">
                  No software update policies yet. Create your first one to start enforcing OS updates.
                </TableCell>
              </TableRow>
            )}
            {policies.map((p) => {
              const kind = String(p.payloads?.[0]?.kind ?? "").toLowerCase();
              const mode = kind.includes("settings") ? "Update behavior" : "Pinned version";
              return (
              <TableRow key={p.id}>
                <TableCell className="font-medium">
                  <button
                    className="hover:underline text-left"
                    onClick={() => navigate(`/software-updates/policies/${p.id}`)}
                  >
                    {p.name}
                  </button>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">{mode}</TableCell>
                <TableCell>{formatDate(p.updatedAt)}</TableCell>
                <TableCell className="text-right">
                  <ActionsDropdown
                    actions={[
                      {
                        label: "View",
                        icon: Eye,
                        onClick: () => navigate(`/software-updates/policies/${p.id}`),
                      },
                      {
                        label: "Edit",
                        icon: Edit,
                        onClick: () => navigate(`/software-updates/policies/${p.id}/edit`),
                      },
                      {
                        label: "Duplicate",
                        icon: Copy,
                        onClick: () =>
                          navigate(`/software-updates/policies/new?duplicate=${p.id}`),
                      },
                      {
                        label: "Delete",
                        icon: Trash2,
                        destructive: true,
                        onClick: () => setToDelete({ id: p.id, name: p.name }),
                      },
                    ]}
                  />
                </TableCell>
              </TableRow>
            );})}
          </TableBody>
        </Table>
      </Card>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Software Update Policy</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &ldquo;{toDelete?.name}&rdquo;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={(e) => {
                e.preventDefault();
                if (toDelete) del.mutate(toDelete.id);
              }}
              disabled={del.isPending}
            >
              {del.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default PoliciesTab;
